import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../framework/services/config.service';

const CACHE_KEY = 'chronolog-effective-auth';

@Injectable({ providedIn: 'root' })
export class AuthorisationService {
  private codes: string[] | null = null;
  private inFlight: Promise<string[]> | null = null;
  private generation = 0;

  constructor(private http: HttpClient, private cs: ConfigService) {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try { this.codes = JSON.parse(cached); } catch { this.codes = null; }
    }
  }

  private getCompanyName(): string {
    const urlParts = window.location.href.split('/');
    const printMatch = window.location.href.match(/print/g);
    const cMatch = window.location.href.includes('/c/');
    const positionToExtract = (printMatch && printMatch.length > 1) || cMatch ? 4 : 3;
    if (urlParts.length > positionToExtract) {
      return urlParts[positionToExtract] || '';
    }
    return '';
  }

  /** Fetch once and cache. Concurrent callers share one HTTP call. */
  load(): Promise<string[]> {
    if (this.codes != null) { return Promise.resolve(this.codes); }
    if (this.inFlight != null) { return this.inFlight; }

    const gen = this.generation;
    this.inFlight = this.fetchWithRetry(gen, 0);

    return this.inFlight;
  }

  private fetchWithRetry(gen: number, attempt: number): Promise<string[]> {
    const MAX_RETRIES = 2;
    const RETRY_DELAY_MS = 500;

    const apiUrl = this.cs.apiEndPoint;
    const companyName = this.getCompanyName();

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'company-name': companyName
      })
    };

    return firstValueFrom(
      this.http.get<string[]>(apiUrl + '/api/Authorisation/Effective', options)
    )
      .then(codes => {
        const result = codes || [];
        if (gen === this.generation) {
          this.codes = result;
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(result));
          this.inFlight = null;
        }
        return result;
      })
      .catch(err => {
        console.warn(`AuthorisationService: load attempt ${attempt + 1} failed`, err?.status ?? err);
        if (gen !== this.generation) {
          return []; // generation changed (logout), stop retrying
        }
        if (attempt < MAX_RETRIES) {
          return new Promise<string[]>(resolve =>
            setTimeout(() => resolve(this.fetchWithRetry(gen, attempt + 1)), RETRY_DELAY_MS)
          );
        }
        // All retries exhausted
        console.warn('AuthorisationService: all retries exhausted, authorisation codes unavailable');
        this.inFlight = null;
        this.codes = null;
        return [];
      });
  }

  /** Synchronous check against the cached list (false if not yet loaded). */
  has(code: string): boolean {
    return this.codes != null && this.codes.indexOf(code) !== -1;
  }

  /** Call on logout. */
  clear(): void {
    this.generation++;
    this.codes = null;
    this.inFlight = null;
    sessionStorage.removeItem(CACHE_KEY);
  }
}
